<template>
  <div class="flex flex-row min-w-screen-lg max-w-screen-xl w-full py-2">
    <!-- START LEFT PANEL -->
    <div
      class="hidden md:flex flex-col w-1/3 bg-white rounded py-5 panel mr-5 h-screen relative py-5 px-3"
    >
      <div class="flex items-center py-3 mb-5 w-1/2 mx-auto">
        <PrimaryButton
          @click="save"
          :disabled="isDocumentTemplateLoading"
          :isLoading="isDocumentTemplateLoading"
          >Save</PrimaryButton
        >
      </div>

      <div class="flex items-center justify-between py-3 px-5">
        <div
          class="flex items-center py-1 px-3 cursor-pointer"
          :class="[sidePanelTab === 'input' ? 'border-b-2 border-brand-green' : '']"
          @click="setSidePanelTab('input')"
        >
          <p :class="[sidePanelTab === 'input' ? 'text-brand-green' : '']">Input Fields</p>
        </div>

        <div
          class="flex items-center py-1 px-3 cursor-pointer"
          :class="[sidePanelTab === 'mappable' ? 'border-b-2 border-brand-green' : '']"
          @click="setSidePanelTab('mappable')"
        >
          <p :class="[sidePanelTab === 'mappable' ? 'text-brand-green' : '']">Mappable Fields</p>
        </div>
      </div>

      <template v-if="sidePanelTab === 'input'">
        <div class="flex flex-col py-2">
          <h5 class="py-3">Signature Fields</h5>

          <div
            class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
            @click="addField('signature')"
          >
            <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
            <p class="text-brand-green">Signature</p>
          </div>

          <div
            class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
            @click="addField('initial')"
          >
            <PencilIcon class="w-5 h-5 text-brand-green mr-2" />
            <p class="text-brand-green">Initial</p>
          </div>

          <h5 class="py-3">Input fields</h5>

          <div
            class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
            @click="addField('checkbox')"
          >
            <img src="@/assets/icons/checkbox.svg" class="w-5 h-5 text-brand-green mr-2" />
            <p class="text-brand-green">Checkbox</p>
          </div>

          <div
            class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
            @click="addField('text')"
          >
            <div class="flex items-center mr-2">
              <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
            </div>
            <div class="flex items-center">
              <p class="text-brand-green">Text Input</p>
            </div>
          </div>

          <div
            class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
            @click="addField('date')"
          >
            <CalendarIcon class="w-5 h-5 text-brand-green mr-2" />
            <div class="flex items-center">
              <p class="text-brand-green">Date Input</p>
            </div>
          </div>

          <h5 class="py-3">Display fields</h5>

          <div
            class="flex items-center border rounded mb-5 px-5 py-2 cursor-pointer w-10/12 mx-auto"
            @click="addField('text_display')"
          >
            <div class="flex items-center mr-2">
              <img src="@/assets/icons/text_field.svg" class="w-5 h-5 text-brand-green" />
            </div>
            <p class="text-brand-green">Text Display</p>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col py-2 px-3 overflow-y-scroll" style="min-height: 30vh">
          <div class="flex items-center pb-5">
            <FormDropdown @select="formSelect" :placeholder="'Select a form to link'" />
          </div>

          <template v-if="isLoadingMappableFields">
            <div class="flex items-center py-3 mx-auto">
              <SmallLoading />
            </div>
          </template>
          <template v-else>
            <template v-if="linkedForm">
              <div
                class="flex flex-col py-2"
                v-for="section in linkedForm.form.sections"
                :key="section"
              >
                <p class="py-1 text-lg">{{ section.label }}</p>

                <div
                  class="flex items-center border rounded mb-3 px-5 py-2 cursor-pointer"
                  @click="addMappableField(field)"
                  v-for="field in section.fields"
                  :key="field.name"
                >
                  <p class="text-brand-green truncate">{{ field.label }}</p>
                </div>
              </div>
            </template>
          </template>
        </div>
      </template>
    </div>
    <!-- END LEFT PANEL -->

    <!-- START DOCUMENT VIEW -->
    <div class="flex w-full md:w-2/3 bg-white panel rounded h-screen py-5 px-5">
      <div class="flex flex-col w-full h-full relative overflow-y-scroll" ref="file-frame">
        <div
          v-for="(file, idx) in getDocumentTemplateTempFiles"
          :key="idx"
          class="relative"
          ref="file-render"
          :id="`${file.name}`"
          @mousemove="trackCursor"
        >
          <div class="flex items-center parent relative">
            <template v-if="file.name.split('.').pop() === 'pdf'">
              <VuePdfEmbed
                :source="fileUrls[file.name]"
                :width="fileFrameWidth"
                @rendered="pdfLoaded(file.name)"
                :disableAnnotationLayer="true"
              />
            </template>
            <template v-else>
              <img
                :src="fileUrls[file.name]"
                class="w-full"
                :ref="`rendered-${file.name}`"
                @load="imageLoaded(file.name)"
              />
            </template>
            <template v-if="overlay[file.name] && overlay[file.name].fields">
              <DocumentDraggableField
                :id="`draggable-${file.name}-${mapperData.x}-${mapperData.y}`"
                v-for="(mapperData, idx) in overlay[file.name].fields"
                :key="`draggable-${file.name}-${mapperData.x}-${mapperData.y}`"
                :field="mapperData"
                @drag-start="dragStart"
                @drag-end="dragEnd($event, file.name, idx)"
                @resize-end="resizeEnd($event, file.name, idx)"
                @set-value="mapperData.label = $event"
                @activated="toggleActivated(mapperData)"
                @remove="removeField(file.name, idx)"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- END DOCUMENT VIEW -->
  </div>

  <!-- START RIGHT SIDE PANEL -->
  <FieldSidePanel :activeField="activeField" @close="toggleActivated" @update="handleFieldUpdate" />
  <!-- END RIGHT SIDE PANEL -->
</template>
<script>
import _ from "lodash"
import { v4 as uuidv4 } from "uuid"

import { mapGetters } from "vuex"
import { DOCUMENT_TEMPLATE_CREATE, APPLICATION_TEMPLATE_GET } from "@/store/types"

import VuePdfEmbed from "vue-pdf-embed"
import { PencilIcon, CalendarIcon } from "@heroicons/vue/outline"

import fieldDefaults from "@/constants/documentFieldDefaults"
import FormDropdown from "../forms/components/FormDropdown.vue"
import FieldSidePanel from "@/views/dashboard/documents/components/FieldSidePanel.vue"

export default {
  components: {
    PencilIcon,
    CalendarIcon,
    VuePdfEmbed,
    FormDropdown,
    FieldSidePanel
  },
  computed: {
    ...mapGetters([
      "getDocumentTemplateTemp",
      "getDocumentTemplateTempFiles",
      "isDocumentTemplateLoading"
    ])
  },
  data() {
    return {
      linkedForm: null,
      isLoadingMappableFields: false,
      sidePanelTab: "input",
      fileUrls: {},
      overlay: {},
      fieldDefaults,
      fileFrameWidth: 0,
      activeField: null,
      cursorPosition: {
        x: 0,
        y: 0
      },
      fileVisible: null,
      isDragging: false,
      signeeOptions: [
        {
          label: "Applicant",
          value: "applicant"
        },
        {
          label: "Landlord",
          value: "landlord"
        },
        {
          label: "Agent",
          value: "agent"
        },
        {
          label: "All",
          value: "all"
        }
      ]
    }
  },
  mounted() {
    this.fileFrameWidth = this.$refs["file-frame"].clientWidth

    if (!this.getDocumentTemplateTempFiles) {
      this.$router.push({ name: "dashboard_new_document_name" })
      return
    }

    //Load images from file url
    this.getDocumentTemplateTempFiles.forEach((file) => {
      this.fileUrls[file.name] = URL.createObjectURL(file)
    })

    // const that = this
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio <= 0) return

      this.fileVisible = entries[0].target.id
    })

    this.$refs["file-render"].forEach((el) => {
      this.observer.observe(el)
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.$refs["file-render"].forEach((el) => {
        this.observer.unobserve(el)
      })
    }
  },
  methods: {
    setSidePanelTab(tab) {
      this.sidePanelTab = tab
    },
    pdfLoaded(fileName) {
      this.overlay[fileName] = {
        fields: []
      }
    },
    toggleActivated(field) {
      if (!this.isDragging && (!field || (field && !["text_display"].includes(field.type)))) {
        this.activeField = field
      }
    },
    imageLoaded(fileName) {
      this.overlay[fileName] = {
        imageWidth: this.$refs[`rendered-${fileName}`][0].clientWidth,
        imageHeight: this.$refs[`rendered-${fileName}`][0].clientHeight,
        fields: []
      }
    },
    dragStart() {
      this.isDragging = true
    },
    dragEnd(positionalData, filename, idx) {
      this.isDragging = false
      this.overlay[filename].fields[idx].x = positionalData.x
      this.overlay[filename].fields[idx].y = positionalData.y

      if (this.activeField) {
        this.activeField = null
      }
    },
    resizeEnd(positionalData, filename, idx) {
      this.overlay[filename].fields[idx].w = positionalData.w
      this.overlay[filename].fields[idx].h = positionalData.h
    },
    removeField(filename, idx) {
      this.overlay[filename].fields = [
        ...this.overlay[filename].fields.filter((f, index) => index !== idx)
      ]

      this.activeField = null
    },
    trackCursor(e) {
      const bounds = document.getElementById(this.fileVisible).getBoundingClientRect()

      this.cursorPosition.x = e.clientX - bounds.left
      this.cursorPosition.y = e.clientY - bounds.top
    },
    addField(type) {
      const fieldData = _.cloneDeep(this.fieldDefaults.find((d) => d.type === type))

      fieldData.x = this.cursorPosition.x
      fieldData.y = this.cursorPosition.y
      fieldData.id = uuidv4()

      this.overlay[this.fileVisible].fields.push(fieldData)
    },
    async save() {
      const { overlay } = this

      const linkedForm = this.linkedForm ? this.linkedForm.slug : null

      //Sort the overlay to be ordered by y index then x
      Object.keys(overlay).forEach((key) => {
        overlay[key].fields = _.orderBy(overlay[key].fields, ["y", "x"], ["asc", "asc"])
      })

      this.fileFrameWidth = this.$refs["file-frame"].clientWidth
      overlay.frameWidth = this.fileFrameWidth

      await this.$store.dispatch(DOCUMENT_TEMPLATE_CREATE, { overlay, linkedForm })
      this.$router.push({ name: "dashboard_documents" })
    },
    async formSelect(form) {
      this.isLoadingMappableFields = true
      try {
        const response = await this.$store.dispatch(APPLICATION_TEMPLATE_GET, { slug: form.slug })
        this.linkedForm = response.data
      } catch (err) {
        console.log(err)
      }
      this.isLoadingMappableFields = false
    },
    addMappableField(field) {
      const fieldData = _.cloneDeep(this.fieldDefaults.find((d) => d.type === "mappable"))
      fieldData.field_name = field.name
      fieldData.field_label = field.label

      fieldData.x = this.cursorPosition.x
      fieldData.y = this.cursorPosition.y

      this.overlay[this.fileVisible].fields.push(fieldData)
    },
    handleFieldUpdate(updates) {
      Object.keys(updates).map((field) => {
        this.activeField[field] = updates[field]
      })
    }
  }
}
</script>
<style scoped>
.panel {
  max-height: 80vh;
  overflow-y: scroll;
}
@media (max-width: 768px) {
  .panel {
    max-height: 100vh;
  }
}
</style>
